import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { leaveTypeLabel } from '../../utils/leaveTypes.js';

// Nicht fest verdrahten: mit einer festen 2026 hätte das Formular ab dem
// 1.1.2027 stumm auf das falsche Jahr gebucht.
const CURRENT_YEAR = new Date().getFullYear();

export function EntitlementModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    employeeId: '',
    leaveTypeId: '',
    year: String(CURRENT_YEAR),
    entitledDays: '25',
  });
  const [error, setError] = useState(null);

  const employees = useQuery({
    queryKey: ['hr', 'employees', 'for-entitlement'],
    queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items),
    enabled: open,
  });
  const leaveTypes = useQuery({
    queryKey: ['hr', 'leave-types'],
    queryFn: () => hrApi.listLeaveTypes().then((r) => r.items),
    enabled: open,
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => hrApi.setEntitlement(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'leave-balances'] });
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    save.mutate({
      employeeId: form.employeeId,
      leaveTypeId: form.leaveTypeId,
      year: Number(form.year),
      entitledDays: Number(form.entitledDays),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('hr.leave.entitlement.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('hr.leave.col.employee')}</span>
          <select className="select" value={form.employeeId} onChange={set('employeeId')} required>
            <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
            {(employees.data || []).map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('hr.leave.col.type')}</span>
          <select className="select" value={form.leaveTypeId} onChange={set('leaveTypeId')} required>
            <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
            {(leaveTypes.data || []).map((lt) => (
              <option key={lt.id} value={lt.id}>{leaveTypeLabel(t, lt)}</option>
            ))}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.leave.entitlement.year')}</span>
            <input className="input" type="number" min="2000" max="2100" value={form.year} onChange={set('year')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('hr.leave.entitlement.days')}</span>
            <input className="input" type="number" min="0" max="366" step="0.5" value={form.entitledDays} onChange={set('entitledDays')} required />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
